package meineglasfaser.frontend.admin.pages.shop

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import meineglasfaser.api.Network
import meineglasfaser.api.ShopAddonGroup
import meineglasfaser.api.YaTableOptions
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedAsc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.YaForm
import meineglasfaser.frontend.common.components.yaform.YaFormNumberInput
import meineglasfaser.frontend.common.components.yaform.YaFormTextArea
import meineglasfaser.frontend.common.components.yaform.YaFormTextInput
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Text

@Page
@Composable
fun AddonGroupsPage(ctx: PageContext) {
    var version by remember { mutableIntStateOf(0) }
    var showCreateModal by remember { mutableStateOf(false) }
    var showUpdateModal by remember { mutableStateOf<ShopAddonGroup?>(null) }

    YaTable(
        header = { H1 { Text("Addon-Gruppen") } },
        idProperty = ShopAddonGroup::id,
        columns = listOf(
            ShopAddonGroup::orderPos named "Position",
            ShopAddonGroup::title named "Title",
            ShopAddonGroup::summary named "Kurzbeschreibung",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(
                Network::orderPos.sortedAsc(),
            ),
        ),
        fetch = { options ->
            BackendApi.post(AdminRoutes.AddonGroups()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onNewEntry = { showCreateModal = true },
        onViewEntry = { showUpdateModal = it },
        version = version,
    )

    if (showCreateModal) {
        CreateAddonGroupModal {
            showCreateModal = false
            version++
        }
    }
    showUpdateModal?.let { addonGroup ->
        UpdateAddonGroupModal(addonGroup) {
            showUpdateModal = null
            version++
        }
    }
}

@Composable
fun CreateAddonGroupModal(onHide: () -> Unit) {
    Modal(onHide) {
        YaForm(
            initial = ShopAddonGroup(
                id = "",
                orderPos = 0,
                title = "",
                summary = "",
            ),
            onSubmitScope = { data ->
                val body = ShopAddonGroup(
                    id = "",
                    orderPos = data.int(ShopAddonGroup::orderPos),
                    title = data.string(ShopAddonGroup::title),
                    summary = data.string(ShopAddonGroup::summary),
                )
                BackendApi.post(AdminRoutes.AddonGroups.New()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Anlegen der Addon-Gruppe: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Addon-Gruppe anlegen") }
            }
            YaFormTextInput(
                prop = ShopAddonGroup::id,
                label = { Text("ID") },
                type = InputType.Text,
                required = false,
                disabled = true,
                placeholder = "(wird automatisch vergeben)"
            )
            YaFormNumberInput(
                prop = ShopAddonGroup::orderPos,
                label = { Text("Position") },
                min = 0,
                required = true,
            )
            YaFormTextInput(
                prop = ShopAddonGroup::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextArea(
                prop = ShopAddonGroup::summary,
                label = { Text("Kurzbeschreibung") },
                rows = 3,
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Anlegen")
        }
    }
}

@Composable
fun UpdateAddonGroupModal(addonGroup: ShopAddonGroup, onHide: () -> Unit) {
    Modal(onHide) {
        YaForm(
            initial = addonGroup,
            onSubmitScope = { data ->
                val body = ShopAddonGroup(
                    id = addonGroup.id,
                    orderPos = data.int(ShopAddonGroup::orderPos),
                    title = data.string(ShopAddonGroup::title),
                    summary = data.string(ShopAddonGroup::summary),
                )
                BackendApi.post(AdminRoutes.AddonGroups.ById(addonGroup.id)) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Aktualisieren der Addon-Gruppe: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Addon-Gruppe bearbeiten") }
            }
            YaFormNumberInput(
                prop = ShopAddonGroup::orderPos,
                label = { Text("Position") },
                min = 0,
                required = true,
            )
            YaFormTextInput(
                prop = ShopAddonGroup::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextArea(
                prop = ShopAddonGroup::summary,
                label = { Text("Kurzbeschreibung") },
                rows = 3,
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Speichern")
        }
    }
}
